import { CircularProgress } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import React, { useState } from 'react';
import { IInventoryReportType } from '../../../../sp-types/InventoryReportType';
import { Material } from '../../../../sp-types/materialType';
import { getTransactions, deleteRow, MaterialTransaction, sxRowsVisibleHover } from './transactionGridUtils';
import { EditOutlined } from '@mui/icons-material';
import { ConfirmDelete } from '../../../../components/UI-Elements/FileUpload/ErrorComponents/ConfirmDelete';

export function formatCurrency(actualPrice: number | undefined): string {
  if (typeof actualPrice !== 'number' || isNaN(actualPrice)) return '0.0';
  return actualPrice.toLocaleString(undefined, { maximumFractionDigits: 0 });
}
export interface TransactionsGridProps {
  inventoryReports: IInventoryReportType[] | undefined | null;
  colorDictionary: Record<string, string>;
  materialsMap: Record<string, Material>;
  refetchInventory: () => void;
  isEditEnabled: boolean;
  onEditRow: (_id: string, _row: MaterialTransaction) => void;
  materialId: string;
  isAdmin: boolean;
  columns: GridColDef[];
  accountId?: string;
  exportType?: 'inventory' | 'salescomm';
  allFutureOrders?: any[];
}
export const TransactionsGrid: React.FC<TransactionsGridProps> = ({
  inventoryReports,
  colorDictionary,
  materialsMap,
  refetchInventory,
  isEditEnabled,
  onEditRow,
  materialId,
  isAdmin,
  columns,
  accountId: _accountId,
  exportType = 'inventory',
  allFutureOrders: _allFutureOrders,
}) => {
  const apiRef = useGridApiRef();
  const [loadingRows, setLoadingRows] = useState<Record<string, boolean>>({});

  const transactionGridBaseColumns: Partial<GridColDef> = {
    headerClassName: 'bg-gray-200 ',
    flex: 1,
    resizable: false,
  };

  const addCols = [
    ...(isAdmin
      ? [
          {
            field: 'action',
            headerName: '',
            sortable: false,
            filterable: false,
            width: 120,
            maxWidth: 120,
            cellClassName: 'w-10',
            renderCell: (params: GridCellParams) => {
              const row = params.row as MaterialTransaction;
              const isLoading = loadingRows[row.id || ''];
              return isEditEnabled ? (
                <div className="action-buttons flex text-blue-600 h-full items-center gap-2">
                  <button onClick={() => setConfirmDelete(row)}>
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <img className="h-6 w-6" src="./assets/icons/blue-flat/delete.svg" alt="Delete" />
                    )}
                  </button>
                  <button onClick={() => onEditRow(row.id?.toString() || '1', row)}>
                    <EditOutlined />
                  </button>
                </div>
              ) : null;
            },
          },
        ]
      : []),
  ];

  const [confirmDelete, setConfirmDelete] = useState<MaterialTransaction | undefined>(undefined);
  return (
    <>
      <DataGrid
        apiRef={apiRef}
        rows={getTransactions(inventoryReports, colorDictionary, materialsMap, materialId)}
        rowCount={inventoryReports?.length || 0}
        columns={[...columns, ...addCols].map((col) => ({ ...transactionGridBaseColumns, ...col }))}
        loading={!inventoryReports}
        pageSizeOptions={[50]}
        paginationMode="server"
        onPaginationModelChange={(val) => console.log('page change', val)}
        rowHeight={32}
        columnHeaderHeight={32}
        hideFooterPagination
        onRowDoubleClick={({ id, row }) => isEditEnabled && onEditRow(id as string, row)}
        getRowClassName={(params) => {
          if ((params.row as any).isGroup) return 'bg-gray-75';
          if ((params.row as MaterialTransaction).ratio && (params.row as MaterialTransaction).ratio! > 0)
            return 'text-gray-400';
          return '';
        }}
        sx={sxRowsVisibleHover}
      />
      {confirmDelete && (
        <ConfirmDelete
          onAction={() => deleteRow(confirmDelete, refetchInventory, setLoadingRows, setConfirmDelete)}
          title={'Confirm delete'}
          actionText={'Confirm'}
          description={
            exportType === 'salescomm'
              ? 'Are you sure you want to delete this Sales Commitment? This will delete the entire order including all months.'
              : 'Are you sure you want to delete selected Report?'
          }
          onCancel={() => setConfirmDelete(undefined)}
        />
      )}
    </>
  );
};

export default TransactionsGrid;
